import React, { useCallback, useEffect, useState } from 'react';
import AgentApprovalsResource, { type AgentApproval } from 'Resources/AgentApprovals';
import InboxProposalCard, { INBOX_CARD_ACTIONS } from '../Chat/InboxProposalCard';
import { notifyAIWorkspaceError } from '../notifyAIWorkspaceError';

type Props = {
	/** The drafts the finished background run left behind, from its completion message. */
	approvalIds: number[];
};

/**
 * WEB-11465 — the drafts a delegated run left, decided in the conversation that
 * delegated it.
 *
 * A background run raises its proposals in its own child conversation, so that
 * is where the cards lived. The parent got a "Needs your approval" banner whose
 * only action was to open the child — a thread the user never started, seeded
 * with a machine-written prompt (WEB-11466). Sitting in the thread you started
 * and being sent somewhere else to say yes is the complaint this whole ticket
 * comes from.
 *
 * Nothing is fetched that the parent did not already know: the completion
 * message it received carries `pendingApprovalIds`
 * (`notifyParentOfDelegatedCompletion`), so this only has to turn those ids
 * back into rows and hand them to the same card the Inbox uses.
 */
const DelegatedApprovalCards = ({ approvalIds }: Props) => {
	const [approvals, setApprovals] = useState<AgentApproval[]>([]);
	// The ids are fixed for the message's lifetime; join them so the effect does
	// not refire on every render over a fresh array literal.
	const key = approvalIds.join(',');

	const load = useCallback(() => {
		if (!approvalIds.length) return;
		AgentApprovalsResource.getMany(approvalIds)
			.then(setApprovals)
			.catch(err => notifyAIWorkspaceError(err, 'getDelegatedApprovals'));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);

	useEffect(load, [load]);

	return (
		<>
			{approvals
				// A row someone already decided elsewhere (the Inbox, the child
				// thread) renders as its own resolved card rather than vanishing, so
				// the thread still says what happened.
				.filter(a => INBOX_CARD_ACTIONS.has(a.action))
				.map(approval => (
					<InboxProposalCard key={approval.id} approval={approval} onResolved={load} />
				))}
		</>
	);
};

export default DelegatedApprovalCards;
